// Lightweight, safe wrapper around Firebase Analytics (GA4) for conversion tracking.
// Page views are already auto-tracked by Firebase enhanced measurement; this file
// adds the custom conversion events that were previously missing.

import { analytics } from '../config/firebase'
import { logEvent } from 'firebase/analytics'

type EventParams = Record<string, string | number | boolean | undefined>

/**
 * Safely log a GA4 event. No-ops if analytics isn't available
 * (SSR, blocked by consent/adblock, or unsupported environment).
 */
export function trackEvent(eventName: string, params: EventParams = {}): void {
  try {
    if (!analytics) return
    logEvent(analytics, eventName, params)
  } catch {
    // Never let analytics break the UI
  }
}

/** Safely fire a Meta Pixel standard event (no-ops if the pixel isn't loaded). */
function trackMeta(event: string, params: EventParams = {}): void {
  try {
    const fbq = (window as any).fbq
    if (typeof fbq === 'function') fbq('track', event, params)
  } catch {
    // never let the pixel break the UI
  }
}

/** Safely fire a Meta Pixel CUSTOM event. */
function trackMetaCustom(event: string, params: EventParams = {}): void {
  try {
    const fbq = (window as any).fbq
    if (typeof fbq === 'function') fbq('trackCustom', event, params)
  } catch {
    // never let the pixel break the UI
  }
}

/** Primary lead-gen action: clicking a "Book a call" / Calendly link. */
export function trackBookCall(location: string, url?: string): void {
  trackEvent('book_call_click', { location, url })
  trackMeta('Schedule', { content_name: 'book_call' })
}

/** Clicked through to buy the book on Amazon (can't track the sale itself). */
export function trackBookBuy(location: string, url?: string): void {
  trackEvent('book_buy_click', { location, url })
  trackMetaCustom('BookClickToAmazon', { location })
}

/** A form lead was submitted (contact, coaching enquiry, etc.). */
export function trackLead(formName: string, params: EventParams = {}): void {
  trackEvent('generate_lead', { form_name: formName, ...params })
  trackMeta('Lead', { content_name: formName })
}

/** Newsletter / freebie / list sign-up. */
export function trackSignUp(method: string, params: EventParams = {}): void {
  trackEvent('sign_up', { method, ...params })
  trackMeta('CompleteRegistration', { content_name: method })
}

/** Completed purchase (GA4 ecommerce 'purchase' + Meta 'Purchase'). */
export function trackPurchase(params: EventParams = {}): void {
  trackEvent('purchase', params)
  trackMeta('Purchase', {
    value: params.value,
    currency: typeof params.currency === 'string' ? params.currency : 'AUD',
  })
}
